/**
 * QuePasa Service
 * Centralized service for all QuePasa API operations
 *
 * Access via:
 * - window.chatwoot.plugins["quepasa-inbox"].service
 * - window.quepasa.service
 *
 * Usage:
 * - quepasa.service.status // Global service status
 * - quepasa.service.checkServerStatus(accountId, inboxId) // Check inbox initialization
 * - quepasa.service.webhook.validate(accountId, inboxId) // Validate webhook
 * - quepasa.service.webhook.create(accountId, inboxId) // Create webhook
 * - quepasa.service.webhook.test(accountId, inboxId, direction) // Test webhook
 * - quepasa.service.webhook.delete(accountId, inboxId) // Delete webhook
 * - quepasa.service.webhook.configure(accountId, inboxId) // Configure webhook
 * - quepasa.service.server.start(accountId, inboxId) // Start server
 * - quepasa.service.server.stop(accountId, inboxId) // Stop server
 * - quepasa.service.server.info(accountId, inboxId) // Get server info
 * - quepasa.service.inbox.current() // Get current inbox
 * - quepasa.service.inbox.get(inboxId) // Get inbox by ID
 */

/* eslint-disable no-console, no-underscore-dangle, class-methods-use-this, no-nested-ternary, func-names */

import { createAuthAxios } from './auth';
import webhookService from './webhook';
import serverService from './server';
import validationService from './validation';
import syncService from './sync';
import contactsService from './contacts';
import groupsService from './groups';
import inboxService from './inbox';
import { getInboxProvider, isQuePasaInbox } from '../helpers/inboxHelpers';

class QuePasaService {
  constructor() {
    this.status = {
      reachable: null, // Global service reachability
      timestamp: null,
    };
    this.cache = new Map(); // Cache by inbox_id: {accountId}-{inboxId} => {initialized, timestamp, error, ...}

    // Initialize service namespaces
    this.webhook = webhookService;
    this.server = serverService;
    this.validation = validationService;
    this.sync = syncService;
    this.contacts = contactsService;
    this.groups = groupsService;
    this.inbox = inboxService;
  }

  /**
   * Get current inbox context from URL and store
   * Always fetches fresh data - no cache
   *
   * @returns {Object} Current context object
   * @property {Object|null} inbox - Current inbox object
   * @property {number|null} inboxId - Inbox ID from URL
   * @property {number|null} accountId - Account ID from URL
   * @property {boolean} isQuePasa - Is this a QuePasa inbox?
   * @property {string|null} provider - Provider name (e.g., 'quepasa')
   * @property {string|null} channelType - Channel type (e.g., 'Channel::Api')
   *
   * @example
   * const current = window.quepasa.current();
   * if (current.isQuePasa) {
   *   console.log('QuePasa inbox:', current.inboxId);
   * }
   */
  current() {
    try {
      // Extract from URL: /app/accounts/{accountId}/settings/inboxes/{inboxId}
      const urlMatch = window.location.pathname.match(
        /\/app\/accounts\/(\d+)\/settings\/inboxes\/(\d+)/
      );

      if (!urlMatch) {
        return {
          inbox: null,
          inboxId: null,
          accountId: null,
          isQuePasa: false,
          provider: null,
          channelType: null,
        };
      }

      const accountId = parseInt(urlMatch[1], 10);
      const inboxId = parseInt(urlMatch[2], 10);

      // Get inbox from service (fresh from store)
      const inbox = this.inbox.get(inboxId);

      if (!inbox) {
        return {
          inbox: null,
          inboxId,
          accountId,
          isQuePasa: false,
          provider: null,
          channelType: null,
        };
      }

      return {
        inbox,
        inboxId,
        accountId,
        isQuePasa: isQuePasaInbox(inbox),
        provider: getInboxProvider(inbox),
        channelType: inbox.channel_type || null,
      };
    } catch (error) {
      console.error('[QuePasa Service] Error getting current:', error);
      return {
        inbox: null,
        inboxId: null,
        accountId: null,
        isQuePasa: false,
        provider: null,
        channelType: null,
      };
    }
  }

  /**
   * Check if QuePasa service is reachable (global health check)
   * @param {Number} accountId - Account ID
   * @returns {Promise<boolean>} true if service is reachable
   */
  async checkServiceHealth(accountId) {
    try {
      const http = createAuthAxios();
      const response = await http.get(
        `/api/v1/accounts/${accountId}/quepasa/service/health`
      );
      return response.data.reachable === true;
    } catch (error) {
      // A 401 here means OUR OWN request to the Chatwoot backend was not
      // authenticated (session/token hiccup) - it says nothing about whether
      // the QuePasa WhatsApp gateway itself is reachable. Treating it as a
      // real outage was flipping inboxes to "unavailable to send" even while
      // the WhatsApp session stayed connected and kept receiving messages.
      // Keep the last known status and let the next poll re-check for real.
      if (error.response?.status === 401) {
        console.warn(
          '[QuePasa Service] Health check got 401 (auth issue on our own API call, not a QuePasa outage) - keeping previous status'
        );
        return this.status?.reachable !== false;
      }

      console.error('[QuePasa Service] Health check failed:', error.message);
      return false;
    }
  }

  /**
   * Check if QuePasa server is initialized for given inbox
   * @param {Number} accountId - Account ID
   * @param {Number} inboxId - Inbox ID
   * @param {Boolean} skipHealthCheck - Skip global health check (already done)
   * @returns {Promise<Object>} { initialized: boolean, error: string|null, cached: boolean }
   */
  async checkServerStatus(accountId, inboxId, skipHealthCheck = false) {
    const cacheKey = `${accountId}-${inboxId}`;
    const now = Date.now();

    // Check cache (valid for 30 seconds)
    const cached = this.cache.get(cacheKey);
    if (cached && now - cached.timestamp < 30000) {
      console.log('[QuePasa Service] Using cached status:', cached);
      return cached;
    }

    // Check if QuePasa service is reachable first (skip if already checked globally)
    if (!skipHealthCheck) {
      const isReachable = await this.checkServiceHealth(accountId);

      // Update global service status
      if (isReachable) {
        this.status = {
          reachable: true,
          timestamp: new Date(now).toISOString(),
        };
      } else {
        this.status = {
          reachable: false,
          timestamp: new Date(now).toISOString(),
          error: 'QuePasa service not reachable',
        };

        // Return early if service is not reachable
        const errorResult = {
          error: 'Service not reachable',
          timestamp: new Date(now).toISOString(),
        };
        this.cache.set(cacheKey, errorResult);
        return errorResult;
      }
    }

    try {
      // Check if server exists in QuePasa using lightweight status endpoint
      // Returns 200 with {initialized: true} if server exists
      // Returns 204 (No Content) if server not found
      const response = await this.server.status(accountId, inboxId);

      // If we got a valid response with initialized property, server is initialized
      if (response?.initialized === true) {
        const result = {
          initialized: true,
          timestamp: new Date(now).toISOString(),
        };

        this.cache.set(cacheKey, result);
        console.log(
          `[QuePasa Service] Server status checked for inbox ${inboxId}:`,
          result
        );
        return result;
      }

      // Empty response or initialized=false means server not found
      const notFoundResult = {
        initialized: false,
        error: 'server_not_found',
        timestamp: new Date(now).toISOString(),
      };

      this.cache.set(cacheKey, notFoundResult);
      console.log(
        `[QuePasa Service] Server status checked for inbox ${inboxId}:`,
        notFoundResult
      );
      return notFoundResult;
    } catch (error) {
      // Error checking individual inbox (service is reachable, but inbox check failed)
      // 204 or 404 means server not initialized
      const isNotInitialized =
        error.response?.status === 204 || error.response?.status === 404;

      const errorResult = {
        ...(isNotInitialized && { initialized: false }), // Only include initialized if 204/404
        error: isNotInitialized
          ? 'server_not_found'
          : error.response?.status === 401
            ? 'Unauthorized - Check authentication'
            : error.message,
        timestamp: new Date(now).toISOString(),
        ...(error.response && {
          errorDetails: {
            status: error.response.status,
            statusText: error.response.statusText,
            message: error.message,
          },
        }),
      };

      // Cache individual inbox error result (global status remains reachable=true)
      this.cache.set(cacheKey, errorResult);

      console.log(
        `[QuePasa Service] Server status checked for inbox ${inboxId}:`,
        errorResult
      );
      return errorResult;
    }
  }

  /**
   * Clear cache for specific inbox or all
   * @param {String} cacheKey - Optional cache key to clear (format: "accountId-inboxId")
   */
  clearCache(cacheKey = null) {
    if (cacheKey) {
      this.cache.delete(cacheKey);
      console.log('[QuePasa Service] Cache cleared for:', cacheKey);
    } else {
      this.cache.clear();
      console.log('[QuePasa Service] All cache cleared');
    }
  }

  /**
   * Get current state for debugging
   */
  getDebugInfo() {
    // Build cache details with inbox-specific results
    const cacheDetails = {};
    this.cache.forEach((value, key) => {
      cacheDetails[key] = {
        initialized: value.initialized,
        timestamp: new Date(value.timestamp).toISOString(),
        error: value.error || null,
      };
    });

    return {
      status: {
        reachable: this.status.reachable,
        timestamp: this.status.timestamp,
        ...(this.status.error && { error: this.status.error }),
      },
      cache: cacheDetails,
      cacheSize: this.cache.size,
      cachedInboxes: Array.from(this.cache.keys()),
    };
  }

  /**
   * Initialize service - Check all QuePasa inboxes automatically
   * @param {Object} store - Vuex store instance
   */
  async initialize(store) {
    if (this._initialized) {
      console.log('[QuePasa Service] Already initialized');
      return;
    }

    console.log('[QuePasa Service] Initializing...');

    if (!store) {
      console.error('[QuePasa Service] Cannot initialize - missing store');
      return;
    }

    this._store = store;
    this._initialized = true;

    try {
      const accountId = store.getters?.getCurrentAccountId;
      if (!accountId) {
        console.log('[QuePasa Service] No account ID available yet');
        return;
      }

      let inboxes = store.getters?.['inboxes/getInboxes'];
      if (!inboxes || inboxes.length === 0) {
        console.log('[QuePasa Service] No inboxes found');
        return;
      }

      console.log(
        '[QuePasa Service] All inboxes:',
        inboxes.map(i => ({
          id: i.id,
          name: i.name,
          channel_type: i.channel_type,
          provider: getInboxProvider(i),
          additional_attributes: i.additional_attributes,
        }))
      );

      // Filter QuePasa inboxes using resilient provider detection.
      let quepasaInboxes = inboxes.filter(inbox => isQuePasaInbox(inbox));

      // Plugin-only cache fallback: if store payload is stale, force network read.
      if (quepasaInboxes.length === 0) {
        try {
          const http = createAuthAxios();
          const freshResponse = await http.get(
            `/api/v1/accounts/${accountId}/inboxes`,
            { params: { _ts: Date.now() } }
          );
          const freshInboxes = freshResponse?.data?.payload || [];

          if (freshInboxes.length > 0) {
            inboxes = freshInboxes;
            quepasaInboxes = inboxes.filter(inbox => isQuePasaInbox(inbox));

            // Keep Vuex in sync so other components also stop reading stale inbox metadata.
            try {
              store.commit('inboxes/SET_INBOXES', freshInboxes);
            } catch (commitError) {
              console.warn(
                '[QuePasa Service] Failed to sync fresh inboxes to store:',
                commitError
              );
            }

            // Also update IndexedDB so that Settings.vue's inboxes/get (with cache=true)
            // does not overwrite Vuex with stale IDB data when the settings page opens.
            // The IDB may have been populated before additional_attributes.provider was added
            // to the serializer (e.g., when sip-channel overrode _inbox.json.jbuilder globally).
            try {
              const dbName = `cw-store-${accountId}`;
              const openReq = window.indexedDB.open(dbName);
              openReq.onsuccess = () => {
                const db = openReq.result;
                try {
                  const tx = db.transaction('inbox', 'readwrite');
                  const objStore = tx.objectStore('inbox');
                  objStore.clear();
                  freshInboxes.forEach(inbox => objStore.put(inbox));
                  tx.oncomplete = () => {
                    console.log(
                      '[QuePasa Service] IDB inbox cache refreshed with correct data'
                    );
                    db.close();
                  };
                } catch (txError) {
                  db.close();
                }
              };
            } catch (idbError) {
              // IDB update is best-effort; Vuex was already updated
            }
          }
        } catch (refreshError) {
          console.warn(
            '[QuePasa Service] Fresh inbox refetch failed:',
            refreshError
          );
        }
      }

      console.log(
        '[QuePasa Service] QuePasa inboxes found:',
        quepasaInboxes.map(i => ({
          id: i.id,
          name: i.name,
          provider: getInboxProvider(i),
        }))
      );

      if (quepasaInboxes.length === 0) {
        console.log('[QuePasa Service] No QuePasa inboxes found');
        return;
      }

      console.log(
        `[QuePasa Service] Found ${quepasaInboxes.length} QuePasa inbox(es), checking status...`
      );

      // Seed reactive state from the server-side monitor snapshot persisted on the inbox.
      // This gives the UI an immediate last-known state before any client-side fetch happens.
      const inboxStates = window.quepasa?.inboxStates;
      const widState = window.quepasa?.widState;
      if (inboxStates || widState) {
        quepasaInboxes.forEach(inbox => {
          const monitorState =
            inbox?.additional_attributes?.quepasa_state_monitor ||
            inbox?.channel?.additional_attributes?.quepasa_state_monitor;

          if (!monitorState) return;

          if (inboxStates && monitorState.current_state) {
            inboxStates[inbox.id] = monitorState.current_state;
          }

          if (widState && inbox.inbox_identifier) {
            const wid = monitorState.wid;
            widState[inbox.inbox_identifier] =
              typeof wid === 'string' && wid.trim().length > 0 ? wid : null;
          }
        });
      }

      // Perform ONE global health check before checking individual inboxes
      const now = Date.now();
      const isReachable = await this.checkServiceHealth(accountId);

      // Update global service status
      if (isReachable) {
        this.status = {
          reachable: true,
          timestamp: new Date(now).toISOString(),
        };
      } else {
        this.status = {
          reachable: false,
          timestamp: new Date(now).toISOString(),
          error: 'QuePasa service not reachable',
        };

        // Only set a hard fallback when we do not already have a last-known state
        // from the server-side monitor snapshot.
        const inboxStatesOnFailure = window.quepasa?.inboxStates;
        if (inboxStatesOnFailure) {
          quepasaInboxes.forEach(inbox => {
            if (!inboxStatesOnFailure[inbox.id]) {
              inboxStatesOnFailure[inbox.id] = 'Stopped';
            }
          });
        }

        console.log(
          '[QuePasa Service] Service not reachable, skipping inbox checks'
        );
        console.log('[QuePasa Service] Initial check completed:', {
          total: quepasaInboxes.length,
          success: 0,
          failed: quepasaInboxes.length,
          reason: 'Service not reachable',
        });
        return;
      }

      // Service is reachable, check status for each QuePasa inbox (skip individual health checks)
      const results = await Promise.allSettled(
        quepasaInboxes.map(
          inbox => this.checkServerStatus(accountId, inbox.id, true) // skipHealthCheck=true
        )
      );

      // Count successful checks (reachable === true)
      const successCount = results.filter(
        r => r.status === 'fulfilled' && !r.value?.error
      ).length;

      const failedCount = results.filter(
        r =>
          r.status === 'rejected' ||
          (r.status === 'fulfilled' && r.value?.error)
      ).length;

      console.log('[QuePasa Service] Initial check completed:', {
        total: quepasaInboxes.length,
        success: successCount,
        failed: failedCount,
      });

      // Fetch WID state for each QuePasa inbox so the SyncTab condition can evaluate reactively.
      // window.quepasa.widState is a Vue reactive object (created in index.ts).
      // Accessing it inside tabs() computed tracks it as a dependency → tabs re-run when wid loads.
      this._fetchWidStates(accountId, quepasaInboxes);
    } catch (error) {
      console.error('[QuePasa Service] Initialization error:', error);
    }
  }

  /**
   * Fetch WID (WhatsApp ID) for each QuePasa inbox via /health endpoint.
   * Stores results in window.quepasa.widState (reactive object) so the SyncTab
   * condition re-evaluates reactively when data arrives.
   *
   * WID format: "5511940122972:45@s.whatsapp.net" → WhatsApp was ever linked
   * Empty/missing wid → server exists but no QR scan done yet
   *
   * @param {Number} accountId - Account ID
   * @param {Array} inboxes - QuePasa inbox objects with id and inbox_identifier
   */
  async _fetchWidStates(accountId, inboxes) {
    const widState = window.quepasa?.widState;
    if (!widState) return;

    await Promise.allSettled(
      inboxes.map(async inbox => {
        const identifier = inbox.inbox_identifier;
        if (!identifier) return;
        try {
          const health = await this.server.health(accountId, inbox.id);
          const wid = health?.wid;
          widState[identifier] =
            typeof wid === 'string' && wid.trim().length > 0 ? wid : null;

          // Normalize raw QuePasa state into stable UI states used by plugin config.
          const inboxStates = window.quepasa?.inboxStates;
          const cachedStatus = this.cache.get(`${accountId}-${inbox.id}`);
          const rawState = health?.state;
          const normalizedState =
            rawState && rawState !== 'N/A'
              ? rawState
              : cachedStatus?.error === 'server_not_found' ||
                  cachedStatus?.initialized === false
                ? 'UnPrepared'
                : 'Disconnected';

          if (inboxStates) {
            inboxStates[inbox.id] = normalizedState;
          }
        } catch {
          // Server not found or unreachable.
          widState[identifier] = null;
          const inboxStatesOnErr = window.quepasa?.inboxStates;
          const cachedStatus = this.cache.get(`${accountId}-${inbox.id}`);
          const fallbackState =
            cachedStatus?.error === 'server_not_found' ||
            cachedStatus?.initialized === false
              ? 'UnPrepared'
              : 'Disconnected';
          if (inboxStatesOnErr) {
            inboxStatesOnErr[inbox.id] = fallbackState;
          }
        }
      })
    );
  }

  /**
   * Ensure service is initialized (lazy initialization)
   * @param {Object} store - Vuex store instance (optional if already initialized)
   * @returns {Promise<boolean>} true if initialized or successfully initialized
   */
  async ensureInitialized(store = null) {
    if (this._initialized) {
      return true;
    }

    const storeToUse = store || this._store;
    if (!storeToUse) {
      console.warn('[QuePasa Service] Cannot initialize - no store available');
      return false;
    }

    await this.initialize(storeToUse);
    return this._initialized;
  }
}

// Create singleton instance
const service = new QuePasaService();

// Expose ready event for components to listen
service._readyPromise = null;
service._readyResolve = null;

service.onReady = function (callback) {
  if (this._initialized) {
    callback();
  } else {
    if (!this._readyPromise) {
      this._readyPromise = new Promise(resolve => {
        this._readyResolve = resolve;
      });
    }
    this._readyPromise.then(callback);
  }
};

// Override initialize to trigger ready event
const originalInitialize = service.initialize.bind(service);
service.initialize = async function (store) {
  await originalInitialize(store);
  if (this._readyResolve) {
    this._readyResolve();
  }
};

export default service;
